import React, { useRef, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { gsap } from 'gsap';
import { getHero } from '../Store/Selectors/selectors';
import * as heroSelectors from '../Store/Selectors/heroSelectors';
import { getActiveQuest } from '../Store/Selectors/questsSelectors';
import { getEffectiveStats, getEquipmentBonus, ALL_SLOTS } from '../Helpers/equipment';
import { StatBar, Label, Num, Coin } from './Common/OkoylUI';

// Horizontal HUD: portrait | bars | stats grid + gauld + class
// Designed for a full-width strip at the top of the game screen.
const HeroStatsDisplay = ({ animate }) => {
    const { t } = useTranslation();
    const hero = useSelector(getHero);
    const activeQuest = useSelector(getActiveQuest);
    const refImage = useRef(null);
    const applyPlayerImageDamage = useRef(false);

    useEffect(() => {
        if (applyPlayerImageDamage.current && refImage.current) {
            const tl = gsap.timeline();
            tl.fromTo(refImage.current, { x: 0 },   { duration: 0.4, ease: 'bounce.out', x: -10 })
              .fromTo(refImage.current, { x: -10 }, { duration: 0.3, ease: 'rough.out',  x: 10 })
              .fromTo(refImage.current, { x: 10 },  { duration: 0.3, ease: 'bounce.out', x: 0 });
            applyPlayerImageDamage.current = false;
            return () => tl.kill();
        }
    }, [animate]);

    useEffect(() => { applyPlayerImageDamage.current = true; }, [animate]);

    if (!hero) return null;

    const eff = getEffectiveStats(hero);
    const bonus = getEquipmentBonus(hero);
    const equippedCount = ALL_SLOTS.filter(s => hero.equipment?.[s]).length;

    const skin = heroSelectors.getSkin(hero);
    const name = heroSelectors.getName(hero) || t('heroStats.fallbackName');
    const classType = (heroSelectors.getClassType(hero) || '').toUpperCase();
    const level = heroSelectors.getLevel(hero) || 1;
    const gold = heroSelectors.getGold(hero) || 0;
    const xp = heroSelectors.getXp(hero) || 0;

    const showBonus = (key) => bonus[key] > 0 ? `+${bonus[key]}` : null;

    return (
        <div
            className="okcard okcard-bare"
            style={{
                display: 'grid',
                gridTemplateColumns: '200px minmax(320px, 420px) 1fr',
                alignItems: 'stretch',
                overflow: 'hidden',
            }}
        >
            {/* Portrait + name plate */}
            <div style={{ position: 'relative', aspectRatio: '4 / 3', overflow: 'hidden' }}>
                <img
                    ref={refImage}
                    src={skin}
                    alt={name}
                    style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        objectPosition: 'center top',
                        background: 'radial-gradient(circle at 50% 30%, #321778, #0c0a12)',
                        display: 'block',
                    }}
                />
                <div style={{ position: 'absolute', top: 8, left: 10, right: 10, display: 'flex', justifyContent: 'space-between', zIndex: 2 }}>
                    <Label color="gold">{name}</Label>
                    <Label>{t('heroStats.labels.level', { level })}</Label>
                </div>
                <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, padding: '14px 10px 6px', background: 'linear-gradient(180deg, transparent, rgba(7,6,10,0.92))', textAlign: 'center' }}>
                    <span style={{ fontFamily: 'var(--font-display)', fontSize: 11, letterSpacing: '0.18em', textTransform: 'uppercase', color: 'var(--okoyl-200)' }}>
                        {classType}
                    </span>
                </div>
            </div>

            {/* Bars column — thick faint gold rail on the left detaches the bars
                from the portrait visually (validated by Jordan, 70px). */}
            <div style={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                padding: '18px 24px',
                gap: 6,
                borderLeft: '70px solid rgba(200,144,42,0.12)',
                borderRight: '1px solid rgba(200,144,42,0.12)',
                minWidth: 0,
            }}>
                <StatBar label={t('heroStats.labels.health')} value={eff.health} max={eff.maxHealth} kind="hp" extra={showBonus('maxHealth')} />
                <StatBar label={t('heroStats.labels.mana')}   value={eff.mana}   max={eff.maxMana}   kind="mp" extra={showBonus('maxMana')} />
                <StatBar label={t('heroStats.labels.memory')} value={xp}         max={Math.max(100, xp + 100)} kind="xp" />
            </div>

            {/* Stats grid + gauld + relics */}
            <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', padding: '14px 18px', gap: 10 }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                    <StatRow label={t('heroStats.labels.strength')}     base={hero.stats.strength}     bonus={bonus.strength} />
                    <StatRow label={t('heroStats.labels.dexterity')}    base={hero.stats.dexterity}    bonus={bonus.dexterity} />
                    <StatRow label={t('heroStats.labels.intelligence')} base={hero.stats.intelligence} bonus={bonus.intelligence} />
                    <StatRow label={t('heroStats.labels.vitality')}     base={hero.stats.vitality}     bonus={bonus.vitality} />
                </div>
                <div style={{ height: 1, background: 'linear-gradient(90deg, transparent, var(--okoyl-500), transparent)', opacity: 0.4 }} />
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--ash-200)' }}>
                    <span><Coin /> {t('heroStats.labels.gauld')}</span>
                    <Num size="md" color="var(--okoyl-200)">{gold}</Num>
                </div>
                {equippedCount > 0 && (
                    <div style={{ fontFamily: 'var(--font-ui)', fontSize: 10, letterSpacing: '0.18em', textTransform: 'uppercase', color: 'var(--okoyl-300)' }}>
                        {equippedCount > 1
                            ? t('heroStats.equippedPlural',   { count: equippedCount })
                            : t('heroStats.equippedSingular', { count: equippedCount })}
                    </div>
                )}
                {activeQuest && (
                    <div style={{
                        marginTop: 4,
                        padding: '8px 10px',
                        background: 'rgba(50,23,120,0.18)',
                        border: '1px solid rgba(154,108,236,0.32)',
                        borderRadius: 'var(--r-sm)',
                    }}>
                        <div style={{ fontFamily: 'var(--font-ui)', fontSize: 9, letterSpacing: '0.18em', textTransform: 'uppercase', color: 'var(--neant-200)', opacity: 0.8 }}>
                            {t('quests.objective')}
                        </div>
                        <div style={{ fontFamily: 'var(--font-serif)', fontStyle: 'italic', fontSize: 12, color: 'var(--ash-100)', marginTop: 2 }}>
                            {activeQuest.stepDescription}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

const StatRow = ({ label, base = 0, bonus = 0 }) => (
    <div>
        <Label>{label}</Label>
        <div>
            <Num size="md" color="var(--okoyl-200)">{base + bonus}</Num>
            {bonus > 0 && (
                <span style={{ marginLeft: 4, fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--okoyl-300)' }}>+{bonus}</span>
            )}
        </div>
    </div>
);

export default HeroStatsDisplay;
